import {
  Arrange,
  Container,
  Dropdown,
  Icon,
  IconButton,
  Logo,
  Spacer,
  SvgActivity,
  SvgArchive,
  SvgBell,
  SvgClock,
  SvgClose,
  SvgDomain,
  SvgHome,
  SvgMenuHide,
  SvgMenuShow,
  SvgSettings,
  SvgStarOutline,
  SvgUser,
  SvgUsers,
  SvgVideoLibrary,
  SvgWatchLater,
  Text,
  Tooltip,
} from '@loomhq/lens';
import cn from 'classnames';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { CLICKED_SHARED_WITH_ME_IN_SIDEBAR } from 'constants/events';
import {
  EXPERIMENT_ACTIVITY_FEED,
  EXP_PERSISTENT_RECORD_BUTTON_SIGNED_IN,
  ROLLOUT_DASHBOARD_WATCH_LATER,
  ROLLOUT_DESTINATION_FEED,
  ROLLOUT_DESTINATION_FOLLOW_SIDEBAR,
  ROLLOUT_DESTINATION_PROFILE_BASIC,
  ROLLOUT_DESTINATION_SIDEBAR,
  ROLLOUT_DESTINATION_TAGS_BASIC,
} from 'constants/featureFlag';
import {
  SPECIAL_FOLDER_ID_ARCHIVED,
  SPECIAL_FOLDER_ID_MY_VIDEOS,
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
} from 'constants/folders';
import {
  ACTIVITY_FEED,
  ARCHIVED,
  FAVORITES_PAGE,
  HISTORY_PAGE,
  LOOMS_PAGE,
  MANAGE_WORKSPACE,
  MY_VIDEOS_PAGE,
  NOTIFICATIONS_PAGE,
  PROFILE_PAGE,
  SHARED_VIDEOS_PAGE,
  TEAM_VIDEOS_PAGE,
  WATCH_LATER_PAGE,
} from 'constants/routes';
import { SHARING_FEATURE_FTUX_CUTOFF } from 'constants/sharingFeatureFtux';
import {
  PRIVATE_WORKSPACE,
  SHARED_WORKSPACE,
  TEAM_WORKSPACE,
} from 'constants/workspaces';
import { selectNavigationScopes } from 'selectors/navigation';
import { selectInTrimMode } from 'selectors/shareVideo';
import { selectCurrentUser } from 'selectors/user';
import * as analytics from 'utilities/analytics';
import { isSafari, isWindows } from 'utilities/device';
import {
  useShareModalFTUXTooltipState,
  useShareWithMeFTUXTooltipState,
} from 'utilities/localStorage/ftux';

import useOnClickOutside from 'hooks/useOnClickOutside';
import { useFeatureFlagValue, useFlagIsActivated } from 'hooks/featureFlag';
import { useWorkspaceCounts } from 'hooks/useCounts';
import {
  useMatchMedia,
  useMatchMobileOnly,
  useMatchTabletOnly,
} from 'hooks/useMatchMedia';

import Divider from './divider';
import FollowsSection from './FollowsSection';
import MenuLink from './MenuLink';
import WorkspaceSelector from '../header/workspace-selector';
import { DropItem } from './DropItem';
import { FadeRoot, FadeText } from './FadeText';
import { useNavigationStore } from './navigation-store';
import { useNotifications } from './notifications/utilities';
import { CALLOUTS, SidebarCallout, useSidebarCallout } from './sidebar-callout';
import { AUTHENTICATED_USER_ACCESS } from '../../../../../shared/constants/scopes';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import HideDuringDowntime from 'components/hide-during-downtime';
import Scopes from 'components/scopes';
import { useShouldShowWaveform } from 'components/share-video/edit-tools-old/hooks';
import ViewerOnboardingStarTooltip from 'components/videos-dashboard-ta/dashboard-onboarding/viewer-onboarding/star-tooltip';

import styles from './styles.module.less';

const RecordButton = React.lazy(() => import('components/record-button'));
const PersistentRecordButton = React.lazy(() =>
  import('components/record-button/persistent-record-button')
);

// Calculates width of logo for height to be 32px
const combinedLogoWidth = (32 * 100) / 30 / 8;

export const navBarAnimationDuration = 300;

const getPrimaryItems = (
  {
    hasPersonalLibraryReadScope,
    hasPersonalLibraryWriteScope,
    hasSharedLibraryReadScope,
    hasSharedLibraryWriteScope,
    hasTeamLibraryReadScope,
    hasTeamLibraryWriteScope,
  },
  { videoCounts, shouldShowFTUXTooltip }
) => [
  {
    icon: SvgUser,
    label: 'Personal',
    path: MY_VIDEOS_PAGE,
    workspace: PRIVATE_WORKSPACE,
    id: SPECIAL_FOLDER_ID_MY_VIDEOS,
    withDrop: hasPersonalLibraryWriteScope,
    show: hasPersonalLibraryReadScope,
  },
  {
    icon: SvgUsers,
    label: 'Shared with me',
    path: SHARED_VIDEOS_PAGE,
    workspace: SHARED_WORKSPACE,
    id: SHARED_VIDEOS_PAGE,
    withDrop: hasSharedLibraryWriteScope,
    show: hasSharedLibraryReadScope,
    onClick: () => {
      analytics.track(CLICKED_SHARED_WITH_ME_IN_SIDEBAR);
    },
    count: videoCounts?.shared_unwatched_video_count,
    shouldShowFTUXTooltip,
  },
  {
    icon: SvgDomain,
    label: 'Team',
    path: TEAM_VIDEOS_PAGE,
    workspace: TEAM_WORKSPACE,
    id: SPECIAL_FOLDER_ID_TEAM_VIDEOS,
    withDrop: hasTeamLibraryWriteScope,
    show: hasTeamLibraryReadScope,
  },
];

const getSecondaryItems = ({
  hasPersonalArchiveReadScope,
  hasTeamArchiveReadScope,
  hasPersonalArchiveWriteScope,
  hasTeamArchiveWriteScope,
  hasFavoritesScope,
  hasWatchLaterScope,
  hasWatchLaterFeatureFlag,
  hasActivityFeedFlag,
  showDestination,
}) => [
  {
    icon: SvgActivity,
    label: 'Recent Activity',
    path: ACTIVITY_FEED,
    withDrop: false,
    show: hasActivityFeedFlag && !showDestination,
  },
  {
    icon: SvgWatchLater,
    label: 'Watch Later',
    path: WATCH_LATER_PAGE,
    withDrop: true,
    show: hasWatchLaterFeatureFlag && hasWatchLaterScope,
  },
  {
    icon: SvgStarOutline,
    label: 'Starred',
    path: FAVORITES_PAGE,
    withDrop: true,
    show: hasFavoritesScope,
    tooltip: <ViewerOnboardingStarTooltip />,
  },
  {
    icon: SvgArchive,
    label: 'Archived',
    path: ARCHIVED,
    id: SPECIAL_FOLDER_ID_ARCHIVED,
    withDrop: hasPersonalArchiveWriteScope || hasTeamArchiveWriteScope,
    show: hasPersonalArchiveReadScope || hasTeamArchiveReadScope,
  },
];

const Noop = props => <div>{props.children}</div>;

export default function Navigation({ withDrop, navBarIsOpenMobile }) {
  const isCompact = useMatchMedia('(max-height: 799px)');
  const isMobile = useMatchMobileOnly();
  const isTablet = useMatchTabletOnly();
  const showTags = useFlagIsActivated({
    flag: ROLLOUT_DESTINATION_TAGS_BASIC,
    activationValues: [true],
  });
  const showProfiles = useFlagIsActivated({
    flag: ROLLOUT_DESTINATION_PROFILE_BASIC,
    activationValues: [true],
  });
  const showHome = useFlagIsActivated({
    flag: ROLLOUT_DESTINATION_FEED,
    activationValues: [true],
  });
  const destinationSidebarConfig = useFeatureFlagValue(
    ROLLOUT_DESTINATION_SIDEBAR
  );
  const showDestination = destinationSidebarConfig?.sidebar;
  const showNotificationsInSidebar =
    destinationSidebarConfig?.notificationsInSidebar;
  const { isCollapsed, isDrawerOpen, toggleDrawer, toggleSidebar } =
    useNavigationStore();
  const navRef = useRef(null);
  const scopes = useSelector(selectNavigationScopes);
  const currentUser = useSelector(selectCurrentUser);
  const inTrimMode = useSelector(selectInTrimMode);

  const isSharePage = window.location.pathname.includes('/share/');
  const [shareModalTooltipDismissed] = useShareModalFTUXTooltipState();
  const shouldShowShareModalTooltipFirst =
    isSharePage && !shareModalTooltipDismissed;
  const [tooltipDismissed] = useShareWithMeFTUXTooltipState();
  const shouldShowFTUXTooltip =
    (!isMobile || navBarIsOpenMobile) &&
    !tooltipDismissed &&
    !shouldShowShareModalTooltipFirst &&
    currentUser &&
    currentUser.createdAt < SHARING_FEATURE_FTUX_CUTOFF;
  const sidebarCallout = useSidebarCallout();
  const isChecklistShown =
    sidebarCallout === CALLOUTS.GET_STARTED_CHECKLIST_CALLOUT;

  const hasWatchLaterFeatureFlag = useFlagIsActivated({
    flag: ROLLOUT_DASHBOARD_WATCH_LATER,
    activationValues: [true],
  });

  const hasActivityFeedFlag = useFlagIsActivated({
    flag: EXPERIMENT_ACTIVITY_FEED,
    activationValues: [true],
  });

  const shouldShowWaveform = useShouldShowWaveform();
  const videoCounts = useWorkspaceCounts('videos');
  const unseenNotificationsCount = useWorkspaceCounts('unseenNotifications');

  useNotifications();

  const MenuItem = withDrop ? DropItem : Noop;

  const displayMode = isCollapsed
    ? 'COLLAPSED'
    : isCompact
    ? 'COMPACT'
    : 'LARGE';

  useOnClickOutside(navRef, () => {
    if (!isCollapsed && isTablet) {
      toggleSidebar(true, { updatePreference: false });
    }
  });

  const PRIMARY_ITEMS = getPrimaryItems(scopes, {
    videoCounts,
    shouldShowFTUXTooltip,
  });
  const SECONDARY_ITEMS = getSecondaryItems({
    hasWatchLaterFeatureFlag,
    hasActivityFeedFlag,
    showDestination,
    ...scopes,
  });

  const hasOverlay = inTrimMode && shouldShowWaveform;

  const settingsButtonWithFtux = (
    <Scopes name={AUTHENTICATED_USER_ACCESS}>
      <MenuButton
        collapsed={isCollapsed}
        name="settings"
        icon={<SvgSettings />}
        items={[
          {
            title: 'Workspace',
            path: MANAGE_WORKSPACE,
            onClick: () => {
              window.location.href = MANAGE_WORKSPACE;
            },
          },
          {
            title: 'Personal',
            path: PROFILE_PAGE,
            onClick: () => {
              window.location.href = PROFILE_PAGE;
            },
          },
        ]}
      >
        <FadeRoot className={styles.menuItemInner}>
          <Icon icon={<SvgSettings />} color="currentColor" />
          <Spacer right="small" />
          <FadeText visible={!isCollapsed}>
            <Text className={styles.linkTitle}>Settings</Text>
          </FadeText>
        </FadeRoot>
      </MenuButton>
    </Scopes>
  );

  const experimentIsAllowed = useCallback(() => {
    const reasons = [];

    if (isMobile) {
      reasons.push('is on mobile');
    }

    if (isSafari) {
      reasons.push('is on Safari');
    }

    if (isWindows) {
      reasons.push('is on Windows');
    }

    return {
      pass: !isMobile && !isSafari && !isWindows,
      failReason: reasons.join(','),
    };
  }, [isMobile]);

  const showPersistentRecordButton = useFlagIsActivated({
    flag: EXP_PERSISTENT_RECORD_BUTTON_SIGNED_IN,
    activationValues: ['variant'],
  });

  return (
    <div
      data-navigation-state={isCollapsed ? 'collapsed' : 'expanded'}
      className={cn(
        styles.nav,
        hasOverlay ? styles.hasOverlay : styles.hasNoOverlay,
        showDestination && styles.showDestination,
        isCollapsed && styles.isCollapsed,
        isDrawerOpen && styles.isOpenMobile
      )}
    >
      {hasOverlay && <div className={styles.navOverlay}></div>}
      <Container
        paddingX="small"
        paddingTop="medium"
        height="100%"
        className="flex flexDirection:column"
      >
        {isMobile && (
          <Spacer bottom="medium">
            <IconButton
              className="ml:small"
              altText="Close navigation"
              icon={<SvgClose />}
              onClick={() => toggleDrawer()}
            />
          </Spacer>
        )}
        <Spacer bottom="large" left="small" top="xsmall">
          <Container height={4} width={isCollapsed ? 4 : combinedLogoWidth}>
            {isCollapsed && <Logo maxWidth={4} variant="symbol" />}
            {!isCollapsed && (
              <a href="/">
                <Logo />
              </a>
            )}
          </Container>
        </Spacer>
        <div className={styles.toggleButton}>
          <Tooltip
            isInline={false}
            content={`${isCollapsed ? 'Show' : 'Hide'} navigation`}
            placement="rightCenter"
          >
            <IconButton
              altText={`${isCollapsed ? 'Show' : 'Hide'} navigation`}
              icon={isCollapsed ? <SvgMenuShow /> : <SvgMenuHide />}
              onClick={() => toggleSidebar()}
            />
          </Tooltip>
        </div>

        <HideDuringDowntime>
          <div className={styles.workspaceSelectorSection}>
            <WorkspaceSelector isCompact={isCollapsed} />
          </div>
        </HideDuringDowntime>

        <div className={cn(styles.menuList, 'py:large')}>
          <Arrange autoFlow="row" gap="2px" columns={['1fr']}>
            {showDestination && (
              <>
                <Arrange
                  autoFlow="row"
                  gap="2px"
                  columns={['1fr']}
                  id="intercom-destination-menu"
                >
                  {showHome && (
                    <MenuLink
                      icon={<SvgHome />}
                      title="Home"
                      url="/home-feed"
                      collapsed={isCollapsed}
                      isMobileCollapsedNav={isMobile && !navBarIsOpenMobile}
                      isReactRouterLink
                    />
                  )}
                  <MenuLink
                    icon={<SvgVideoLibrary />}
                    title="Looms"
                    url={LOOMS_PAGE}
                    collapsed={isCollapsed}
                    isMobileCollapsedNav={isMobile && !navBarIsOpenMobile}
                    isReactRouterLink
                  />
                  {showNotificationsInSidebar && (
                    <MenuLink
                      icon={<SvgBell />}
                      title="Notifications"
                      url={NOTIFICATIONS_PAGE}
                      collapsed={isCollapsed}
                      count={unseenNotificationsCount}
                      isMobileCollapsedNav={isMobile && !navBarIsOpenMobile}
                      isReactRouterLink
                    />
                  )}
                  <MenuLink
                    icon={<SvgClock />}
                    title="History"
                    url={HISTORY_PAGE}
                    collapsed={isCollapsed}
                    isMobileCollapsedNav={isMobile && !navBarIsOpenMobile}
                    isReactRouterLink
                  />
                </Arrange>
                {settingsButtonWithFtux}

                <FeatureFlaggedComponent
                  flag={ROLLOUT_DESTINATION_FOLLOW_SIDEBAR}
                  activationValues={[true]}
                >
                  {(showTags || showProfiles) && (
                    <>
                      <Divider />
                      <FollowsSection
                        isCollapsed={isCollapsed}
                        isMobile={isMobile}
                        navBarIsOpenMobile={navBarIsOpenMobile}
                        showTags={showTags}
                        showProfiles={showProfiles}
                      />
                    </>
                  )}
                </FeatureFlaggedComponent>
              </>
            )}
            {!showDestination && (
              <>
                <div className={styles.librariesHeading}>
                  <Spacer bottom="small" left="xsmall">
                    <Text fontWeight="medium" color="bodyDimmed">
                      Libraries
                    </Text>
                  </Spacer>
                </div>
                {PRIMARY_ITEMS.map(
                  item =>
                    item.show && (
                      <MenuItem key={item.label} item={item}>
                        <MenuLink
                          icon={<item.icon />}
                          title={item.label}
                          url={item.path}
                          collapsed={isCollapsed}
                          count={item.count}
                          onClick={item.onClick}
                          shouldShowFTUXTooltip={item.shouldShowFTUXTooltip}
                          isMobileCollapsedNav={isMobile && !navBarIsOpenMobile}
                        />
                      </MenuItem>
                    )
                )}
                <Spacer top="xlarge" />
                {SECONDARY_ITEMS.map(
                  item =>
                    item.show && (
                      <MenuItem key={item.label} item={item}>
                        <MenuLink
                          icon={<item.icon />}
                          title={item.label}
                          url={item.path}
                          collapsed={isCollapsed}
                        />
                      </MenuItem>
                    )
                )}
                {settingsButtonWithFtux}
              </>
            )}
          </Arrange>
        </div>

        <div className="flex flexDirection:column grow:1 justify:flexEnd">
          {showPersistentRecordButton !== undefined && (
            <>
              {showPersistentRecordButton &&
              experimentIsAllowed().pass &&
              !isChecklistShown ? (
                <>
                  <SidebarCallout displayMode={displayMode} />
                  <Container
                    position="relative"
                    left="calc(-1 * var(--lns-space-small))"
                    paddingTop="small"
                    paddingBottom={1.5}
                  >
                    <Suspense fallback={null}>
                      <RecordButton>
                        <PersistentRecordButton isCollapsed={isCollapsed} />
                      </RecordButton>
                    </Suspense>
                  </Container>
                </>
              ) : (
                <SidebarCallout displayMode={displayMode} />
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
}

export function MenuButton({ name, items, children, collapsed }) {
  const [open, setOpen] = useState(false);
  const panelId = `${name}-panel`;

  useEffect(() => {
    setOpen(false);
  }, [collapsed]);

  const buttonTrigger = (
    <button
      className={cn(styles.menuItem)}
      aria-expanded={collapsed ? undefined : open}
      aria-haspopup={collapsed ? 'true' : undefined}
      aria-controls={panelId}
      onClick={collapsed ? undefined : () => setOpen(!open)}
    >
      {children}
    </button>
  );

  if (collapsed) {
    return <Dropdown trigger={buttonTrigger} options={items} />;
  }

  return (
    <div>
      {buttonTrigger}
      <div className={styles.menuPanel} id={panelId} hidden={!open}>
        {items.map(item => (
          <div key={item.path}>
            <a
              className={styles.menuItem}
              href={item.path}
              aria-current={
                window.location.pathname.startsWith(item.path)
                  ? 'page'
                  : undefined
              }
            >
              <Text fontWeight="medium">{item.title}</Text>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
